import { useEffect, useState } from "react";
import Button from "components/elements/Button/Button";
import Loading from "components/elements/Loading/Loading";
import OverflowTooltip from "components/elements/Tooltips/OverflowTooltip";
import { FetchError } from "lib/actions/fetcher";
import { popToastSuccess } from "lib/actions/toast";
import {
  AutodeskImportSelection,
  AutodeskImportState,
} from "../autodeskImportReducer";
import { previewAutodeskSelection } from "../lib/actions/previewAutodeskSelection";
import { importAutodeskSelection } from "../lib/actions/importAutodeskSelection";
import { AutodeskImportSelectionModel } from "../lib/types/AutodeskImportSelectionModel";
import {
  AutodeskImportPreviewModel,
  AutodeskSkippedFileModel,
} from "../lib/types/AutodeskImportPreviewModel";
import AutodeskRequestError from "../controls/AutodeskRequestError";
import { AutodeskImportStepProps } from "./AutodeskImportStepProps";

type Failure = { error: unknown; onRetry(): void };

const skipReasonLabels: Record<AutodeskSkippedFileModel["reason"], string> = {
  NoDownloadableFile: "Nothing to download",
  AlreadyImported: "Already imported",
  NotFound: "Not found",
};

export default function AutodeskImportReviewStep(
  p: AutodeskImportStepProps & { projectId: number; onClose(): void },
) {
  const [failure, setFailure] = useState<Failure | null>(null);
  const { selection, preview } = p.state;
  const selectionModel = toSelectionModel(p.state, p.projectId);

  useEffect(() => {
    if (!preview) loadPreview();
  }, []);

  async function loadPreview() {
    setFailure(null);
    try {
      p.dispatch({
        type: "PREVIEW_LOADED",
        selection,
        preview: await previewAutodeskSelection(selectionModel),
      });
    } catch (error) {
      setFailure({ error, onRetry: loadPreview });
    }
  }

  async function onConfirm() {
    setFailure(null);
    p.dispatch({ type: "IMPORT_STARTED" });
    try {
      const result = await importAutodeskSelection(selectionModel);
      if (result.status === "refused") {
        p.dispatch({
          type: "IMPORT_REFUSED",
          selection,
          refusal: result.refusal,
        });
        return;
      }
      const hasFailures = result.outcomes.some((outcome) => outcome.failure);
      if (hasFailures) {
        p.dispatch({
          type: "IMPORT_FINISHED_WITH_FAILURES",
          selection,
          outcomes: result.outcomes,
        });
        return;
      }
      popToastSuccess(`Started ${countImports(result.outcomes.length)}`);
      p.onClose();
    } catch (error) {
      setFailure({ error, onRetry: onConfirm });
    } finally {
      p.dispatch({ type: "IMPORT_STOPPED" });
    }
  }

  if (failure)
    return (
      <SelectionFailure
        failure={failure}
        workspaceId={p.workspaceId}
        onReconnected={p.onReconnected}
      />
    );
  if (!preview) return <Loading />;

  const fileCount = preview.covered.length;
  const canConfirm = !preview.refusal && fileCount > 0;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-thin">
        Import{" "}
        <span className="font-normal">{describeSelection(selection)}</span>
      </h1>
      {preview.refusal ? (
        <p className="rounded-md bg-amber-50 p-4 text-sm">
          {preview.refusal.message}
        </p>
      ) : (
        <PreviewSummary preview={preview} />
      )}
      <footer className="flex items-center justify-end gap-4 border-t pt-4">
        {!preview.refusal && fileCount === 0 && (
          <span className="text-sm text-gray-500">Nothing to import</span>
        )}
        <Button
          type="button"
          theme="primary-outline"
          slim
          disabled={p.state.isImporting}
          onClick={() =>
            p.dispatch({
              type: "SHOW_FOLDER",
              depth: p.state.folderPath.length,
            })
          }
        >
          Back
        </Button>
        <Button
          type="button"
          slim
          disabled={!canConfirm || p.state.isImporting}
          onClick={onConfirm}
        >
          {getConfirmLabel(preview, p.state.isImporting)}
        </Button>
      </footer>
    </div>
  );
}

function PreviewSummary(p: { preview: AutodeskImportPreviewModel }) {
  const unsupported = Object.entries(p.preview.unsupportedCountsByExtension);

  return (
    <div className="space-y-6 text-sm font-light">
      <section>
        <h2 className="mb-2 font-medium">To import</h2>
        <ExtensionCounts counts={p.preview.coveredCountsByExtension} />
      </section>
      {p.preview.skipped.length > 0 && (
        <section>
          <h2 className="mb-2 font-medium">Left out</h2>
          <ul className="space-y-1">
            {p.preview.skipped.map((file) => (
              <li
                key={`${file.folderPath}/${file.name}`}
                className="flex justify-between gap-4"
              >
                <OverflowTooltip className="min-w-0">
                  {file.name}
                  <span className="ml-2 text-gray-400">{file.folderPath}</span>
                </OverflowTooltip>
                <span className="shrink-0 text-gray-500">
                  {skipReasonLabels[file.reason]}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {unsupported.length > 0 && (
        <section>
          <h2 className="mb-2 font-medium">Not supported</h2>
          <ExtensionCounts counts={p.preview.unsupportedCountsByExtension} />
        </section>
      )}
    </div>
  );
}

function ExtensionCounts(p: { counts: Record<string, number> }) {
  const entries = Object.entries(p.counts);
  if (entries.length === 0) return <p className="text-gray-500">No files</p>;

  return (
    <ul className="space-y-1">
      {entries.map(([extension, count]) => (
        <li key={extension}>
          {countFiles(count)}{" "}
          <span className="text-gray-500">{formatExtension(extension)}</span>
        </li>
      ))}
    </ul>
  );
}

function SelectionFailure(p: {
  failure: Failure;
  workspaceId: number;
  onReconnected(): void;
}) {
  const message = describeRejection(p.failure.error);
  if (!message)
    return (
      <AutodeskRequestError
        error={p.failure.error}
        workspaceId={p.workspaceId}
        onRetry={p.failure.onRetry}
        onReconnected={p.onReconnected}
      />
    );

  return (
    <div className="space-y-4 text-center text-sm">
      <p>{message}</p>
      <Button
        type="button"
        theme="primary-outline"
        slim
        onClick={p.failure.onRetry}
      >
        Retry
      </Button>
    </div>
  );
}

function describeRejection(error: unknown): string | null {
  if (!(error instanceof FetchError)) return null;
  if (error.status === 403)
    return "You don't have access to import into this project.";
  if (error.status === 422 && typeof error.response.data === "string")
    return error.response.data;
  return null;
}

function toSelectionModel(
  state: AutodeskImportState,
  projectId: number,
): AutodeskImportSelectionModel {
  return {
    projectId,
    autodeskConnectedAccountId: state.accountId,
    hubId: state.hub.id,
    autodeskProjectId: state.project.id,
    scope: state.selection.scope,
    id: state.selection.id,
  };
}

function getConfirmLabel(
  preview: AutodeskImportPreviewModel,
  isImporting: boolean,
) {
  if (isImporting) return "Starting imports...";
  if (preview.refusal) return "Import";
  return `Import ${countFiles(preview.covered.length)}`;
}

function describeSelection(selection: AutodeskImportSelection) {
  if (selection.scope === "Item") return selection.name;
  if (selection.scope === "Folder") return `the ${selection.name} folder`;
  return `${selection.name} and its subfolders`;
}

function formatExtension(extension: string) {
  return extension.replace(/^\./, "").toUpperCase() || "No extension";
}

function countFiles(count: number) {
  return count === 1 ? "1 file" : `${count} files`;
}

function countImports(count: number) {
  return count === 1 ? "1 import" : `${count} imports`;
}
